import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { notificar } from '../utils/notify';
import '../styles/fmp.css';

const fmt = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(valor));
};

const StatusBadge = ({ status }) => {
  const map = {
    'VENCIDA/ATRASADA': 'bg-danger',
    'VENCE HOJE': 'bg-warning text-dark',
    'A VENCER': 'bg-success',
  };
  return (
    <span className={`badge ${map[status] || 'bg-secondary'}`} style={{ fontSize: '11px' }}>
      {status === 'VENCIDA/ATRASADA' ? 'VENCIDA' : status}
    </span>
  );
};

const TabelaContas = ({ lista, tipo }) => {
  const isReceber = tipo === 'receber';
  const corValor = (item) => isReceber ? 'text-success' : (item.status === 'VENCIDA/ATRASADA' ? 'text-danger' : 'text-dark');
  return (
    <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
      {/* Desktop: tabela */}
      <table className="table table-sm table-hover mb-0 d-none d-md-table">
        <thead className="table-light sticky-top">
          <tr>
            <th style={{ width: '18%' }}>Vencimento</th>
            <th style={{ width: '38%' }}>{isReceber ? 'Cliente' : 'Fornecedor'}</th>
            <th style={{ width: '20%' }} className="text-end">Valor</th>
            <th style={{ width: '15%' }} className="text-center">Status</th>
            <th style={{ width: '9%' }} className="text-center">Dias</th>
          </tr>
        </thead>
        <tbody>
          {lista.map((item, idx) => (
            <tr key={idx}>
              <td><small>{item.vencimento}</small></td>
              <td><small>{item.nome}</small></td>
              <td className="text-end">
                <strong className={corValor(item)}>{fmt(item.valor)}</strong>
              </td>
              <td className="text-center"><StatusBadge status={item.status} /></td>
              <td className="text-center">
                {item.dias_atraso > 0
                  ? <small className="text-danger fw-bold">{item.dias_atraso}d</small>
                  : <small className="text-muted">—</small>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile: cards */}
      <div className="d-md-none">
        {lista.map((item, idx) => (
          <div key={idx} className="px-3 py-2 border-bottom">
            <div className="d-flex justify-content-between align-items-start">
              <span className="fw-semibold me-2" style={{ fontSize: '0.82rem', wordBreak: 'break-word', flex: 1 }}>
                {item.nome}
              </span>
              <strong className={`flex-shrink-0 ${corValor(item)}`} style={{ fontSize: '0.85rem' }}>
                {fmt(item.valor)}
              </strong>
            </div>
            <div className="d-flex align-items-center gap-2 mt-1" style={{ flexWrap: 'wrap' }}>
              <small className="text-muted">{item.vencimento}</small>
              <StatusBadge status={item.status} />
              {item.dias_atraso > 0 && (
                <small className="text-danger fw-bold">{item.dias_atraso}d</small>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CardResumo = ({ icon, iconColor, titulo, subtitulo, valor, valorColor, qtd }) => (
  <div className={`card border-0 shadow-sm h-100 border-start border-4 border-${iconColor}`}>
    <div className="card-body text-center py-3">
      <i className={`fas ${icon} fa-2x text-${iconColor} mb-2`}></i>
      <h6 className="text-muted small mb-1">{titulo}</h6>
      {subtitulo && <small className="text-muted d-block mb-1">{subtitulo}</small>}
      <h4 className={`text-${valorColor || iconColor} mb-0`}>{fmt(valor)}</h4>
      {qtd !== undefined && <small className="text-muted">{qtd} título(s)</small>}
    </div>
  </div>
);

const Posicao_dia = () => {
  const { cliente_id } = useParams();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [dadosCliente, setDadosCliente] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [abertoPagarAtraso, setAbertoPagarAtraso] = useState(true);
  const [abertoPagarHoje, setAbertoPagarHoje] = useState(true);
  const [abertoReceberAtraso, setAbertoReceberAtraso] = useState(true);
  const [abertoReceberJanela, setAbertoReceberJanela] = useState(false);
  const [abertoReceberJanela1, setAbertoReceberJanela1] = useState(true);
  const [abertoReceberJanela2, setAbertoReceberJanela2] = useState(false);
  const [abertoPagarJanela, setAbertoPagarJanela] = useState(false);
  const [abertoPagarJanela1, setAbertoPagarJanela1] = useState(true);
  const [abertoPagarJanela2, setAbertoPagarJanela2] = useState(false);

  const buscarDados = async () => {
    setCarregando(true);
    setMensagem('');
    const token = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/posicao-dia-cliente/`,
        {
          params: { cliente_id },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 0
        }
      );
      if (response.data.success) {
        setDados(response.data.dados);
        const agora = new Date();
        setUltimaAtualizacao(
          `${agora.toLocaleDateString('pt-BR')} às ${agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
        );
      } else {
        setMensagem(`${response.data.erro || 'Erro ao carregar dados'}`);
      }
    } catch (err) {
      setMensagem('Erro ao conectar com o servidor');
    } finally {
      setCarregando(false);
    }
  };

  const carregarDadosCliente = async () => {
    const token = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/cliente-bpo/${cliente_id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setDadosCliente(response.data.cliente);
      }
    } catch {}
  };

  const buscarUltimaAtualizacao = async () => {
    const token = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/ultima-atualizacao-contaazul/${cliente_id}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setUltimaAtualizacao(response.data.ultima_atualizacao);
      }
    } catch {}
  };

  const handleAtualizar = async () => {
    setAtualizando(true);
    setMensagem('');
    const token = localStorage.getItem('access');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/disparar-atualizacao-contaazul/${cliente_id}/`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 1260000 }
      );
      if (response.data.success) {
        setMensagem('Sincronização concluída com sucesso!');
        await buscarDados();
      } else {
        setMensagem(`${response.data.mensagem || 'Erro na atualização.'}`);
      }
    } catch {
      setMensagem('Erro ao iniciar atualização. Tente novamente.');
    } finally {
      setAtualizando(false);
      buscarUltimaAtualizacao();
    }
  };

  useEffect(() => {
    buscarDados();
    carregarDadosCliente();
    buscarUltimaAtualizacao();
  }, [cliente_id]);

  const contasCorrente = dados?.contas_bancarias?.filter(
    c => classeConta(c) === 'corrente'
  ) || [];

  const dadosGrafico = useMemo(() => {
    if (!dados) return [];

    const parseDate = (str) => {
      if (!str) return null;
      if (str.includes('/')) {
        const [d, m, y] = str.split('/');
        if (d && m && y) return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      if (str.includes('-')) {
        const [y, m, d] = str.split('-');
        if (y && m && d) return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      return null;
    };

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    // Ponto inicial com saldo atual antes das movimentações
    const pontos = [{ label: 'Atual', entradas: 0, saidas: 0, saldo: parseFloat(dados.saldo_corrente_total || 0), isHoje: true }];
    let saldo = parseFloat(dados.saldo_corrente_total || 0);

    for (let i = 0; i <= 30; i++) {
      const dia = new Date(hoje);
      dia.setDate(hoje.getDate() + i);
      const label = dia.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

      const entradas = (dados.contas_receber_15_15 || [])
        .filter(item => { const dt = parseDate(item.vencimento); return dt && dt.getTime() === dia.getTime(); })
        .reduce((s, item) => s + parseFloat(item.valor || 0), 0);

      const saidas = (dados.contas_pagar_15_15 || [])
        .filter(item => { const dt = parseDate(item.vencimento); return dt && dt.getTime() === dia.getTime(); })
        .reduce((s, item) => s + parseFloat(item.valor || 0), 0);

      saldo = saldo + entradas - saidas;
      pontos.push({ label, entradas, saidas, saldo });
    }

    return pontos;
  }, [dados]);

  const janelasSplit = useMemo(() => {
    const empty = { receber1: [], receber2: [], pagar1: [], pagar2: [], d15Label: '' };
    if (!dados) return empty;

    const parseDate = (str) => {
      if (!str) return null;
      if (str.includes('/')) {
        const [d, m, y] = str.split('/');
        if (d && m && y) return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      if (str.includes('-')) {
        const [y, m, d] = str.split('-');
        if (y && m && d) return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      }
      return null;
    };

    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    const d15 = new Date(hoje); d15.setDate(hoje.getDate() + 15);
    const d15Label = d15.toLocaleDateString('pt-BR');

    const split = (lista) => [
      lista.filter(item => { const dt = parseDate(item.vencimento); return dt && dt <= d15; }),
      lista.filter(item => { const dt = parseDate(item.vencimento); return dt && dt > d15; }),
    ];

    const [receber1, receber2] = split(dados.contas_receber_15_15 || []);
    const [pagar1, pagar2] = split(dados.contas_pagar_15_15 || []);
    return { receber1, receber2, pagar1, pagar2, d15Label };
  }, [dados]);

  const somaValor = (lista) => lista.reduce((s, i) => s + parseFloat(i.valor || 0), 0);

  return (
    <div className="container-fluid mt-4">

      {/* ── HEADER ── */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                {/* Lado Esquerdo - Info Cliente */}
                <div className="flex-grow-1">
                  <div className="mb-4">
                    <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                      Financial Management Platform
                    </span>
                    {dadosCliente?.tipo_plano && (
                      <span
                        className={`badge ${dadosCliente.tipo_plano === 'Premium' ? 'bg-success' : 'bg-warning text-dark'}`}
                        style={{ fontSize: '15px', fontWeight: '500' }}
                      >
                        <i className={`fas ${dadosCliente.tipo_plano === 'Premium' ? 'fa-crown' : 'fa-flask'} me-1`}></i>
                        {dadosCliente.tipo_plano === 'Premium' ? 'Versão Premium' : 'Versão Demo'}
                      </span>
                    )}
                  </div>

                  <div className="d-flex align-items-center mb-2">
                    <i className="fas fa-calendar-day header-icon me-2"></i>
                    <h2 className="titulo-pagina m-0">
                      Posição do Dia — {dadosCliente?.nome || dados?.nome_cliente || '...'}
                    </h2>
                  </div>

                  <p className="mb-0 opacity-75" style={{ color: '#000', fontSize: '0.85rem' }}>
                    <i className="fas fa-clock me-1"></i>
                    Referência: {dados?.data_referencia || new Date().toLocaleDateString('pt-BR')}
                    {dados?.periodo_15_15 && (
                      <span className="ms-3">
                        Janela 15+15: {dados.periodo_15_15.inicio} até {dados.periodo_15_15.fim}
                      </span>
                    )}
                  </p>
                </div>

                {/* Lado Direito - Botões */}
                <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                  <button
                    className="btn btn-sm"
                    onClick={() => navigate(
                      dadosCliente?.sistema?.toLowerCase() === 'conta_azul'
                        // ?dashboard=1: o cliente 19 abre na Posição do Dia por
                        // padrão, e sem este pedido explícito o dashboard devolvia
                        // o clique para cá na hora
                        ? `/dashboard-conta-azul/${cliente_id}?dashboard=1`
                        : `/dashboard-cliente/${cliente_id}`
                    )}
                    style={{
                      backgroundColor: '#00205b',
                      borderColor: '#00205b',
                      color: '#fff',
                      fontSize: '0.80rem',
                      padding: '5px 14px',
                    }}
                  >
                    <i className="fas fa-chart-line me-1"></i>
                    Dashboard
                  </button>

                  <button
                    className="btn btn-sm"
                    onClick={handleAtualizar}
                    disabled={atualizando}
                    style={{
                      backgroundColor: atualizando ? '#1c702e' : '#1f7a35',
                      borderColor: '#1e7e34',
                      color: '#fff',
                      fontSize: '0.80rem',
                      padding: '5px 14px',
                    }}
                  >
                    <i className={`fas fa-sync-alt me-1${atualizando ? ' fa-spin' : ''}`}></i>
                    {atualizando ? 'Atualizando...' : 'Atualizar'}
                  </button>
                  {ultimaAtualizacao && (
                    <span style={{ fontSize: '0.82rem', color: '#111', fontWeight: '500' }}>
                      <i className="fas fa-clock me-1"></i>
                      Última atualização: {ultimaAtualizacao}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mensagens agora são exibidas como toast (react-toastify) */}

      {/* ── LOADING ── */}
      {carregando && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
          <p className="mt-3 text-muted">Carregando posição do dia...</p>
        </div>
      )}

      {dados && !carregando && (
        <>
          {/* ── CARDS RESUMO ── */}
          <div className="row mb-4 g-3">
            <div className="col-lg-3 col-md-6">
              <CardResumo
                icon="fa-university"
                iconColor="primary"
                titulo="Saldo em Contas Correntes"
                subtitulo="Disponível hoje"
                valor={dados.saldo_corrente_total}
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <CardResumo
                icon="fa-exclamation-triangle"
                iconColor="danger"
                titulo="A Pagar em Atraso"
                subtitulo="Vencidos não pagos"
                valor={dados.total_pagar_atraso}
                qtd={dados.qtd_pagar_atraso}
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <CardResumo
                icon="fa-calendar-check"
                iconColor="warning"
                valorColor="warning"
                titulo="A Pagar Hoje"
                subtitulo="Vence neste dia"
                valor={dados.total_pagar_hoje}
                qtd={dados.qtd_pagar_hoje}
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 shadow-sm h-100 border-start border-4 border-info">
                <div className="card-body text-center py-3">
                  <i className="fas fa-hand-holding-usd fa-2x text-info mb-2"></i>
                  <h6 className="text-muted small mb-1">Janela 15+15 dias</h6>
                  <small className="text-muted d-block mb-1">Receber: {fmt(dados.total_receber_15_15)}</small>
                  <small className="text-muted d-block">Pagar: {fmt(dados.total_pagar_15_15)}</small>
                  <h5 className={`mt-2 mb-0 ${dados.total_receber_15_15 - dados.total_pagar_15_15 >= 0 ? 'text-success' : 'text-danger'}`}>
                    Saldo: {fmt(dados.total_receber_15_15 - dados.total_pagar_15_15)}
                  </h5>
                </div>
              </div>
            </div>
          </div>

          {/* ── SALDOS BANCÁRIOS ── */}
          <div className="row mb-4">
            <div className="col-lg-6 mb-3">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white py-2">
                  <h6 className="card-title mb-0">
                    <i className="fas fa-university me-2 text-primary"></i>
                    Contas Correntes / Digitais
                  </h6>
                </div>
                <div className="card-body p-0">
                  {contasCorrente.length === 0
                    ? <p className="text-muted text-center py-3 mb-0">Nenhuma conta configurada</p>
                    : (
                      <table className="table table-sm mb-0">
                        <tbody>
                          {contasCorrente.map((c, i) => (
                            <tr key={i}>
                              <td className="ps-3">
                                <i className={`${c.icone} me-2`} style={{ color: c.cor }}></i>
                                <small className="fw-bold">{c.nome}</small>
                                {c.tipo && <span className="badge bg-primary bg-opacity-25 text-primary ms-1" style={{ fontSize: '10px' }}>{c.tipo}</span>}
                              </td>
                              <td className="text-end pe-3">
                                <strong className={c.saldo_disponivel < 0 ? 'text-danger' : 'text-primary'}>
                                  {fmt(c.saldo_disponivel)}
                                </strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="table-primary">
                            <td className="ps-3"><small><strong>TOTAL DISPONÍVEL</strong></small></td>
                            <td className="text-end pe-3"><small><strong>{fmt(dados.saldo_corrente_total)}</strong></small></td>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                </div>
              </div>
            </div>

          </div>

          {/* ── CONTAS A PAGAR EM ATRASO ── */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm border-start border-danger border-4">
                <div
                  className="card-header bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setAbertoPagarAtraso(!abertoPagarAtraso)}
                >
                  <h5 className="card-title mb-0">
                    <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                    Contas a Pagar em Atraso
                    <span className="badge bg-danger ms-2">{dados.qtd_pagar_atraso}</span>
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <strong className="text-danger">{fmt(dados.total_pagar_atraso)}</strong>
                    <i className={`fas fa-chevron-${abertoPagarAtraso ? 'up' : 'down'} text-muted`}></i>
                  </div>
                </div>
                {abertoPagarAtraso && (
                  <div className="card-body p-0">
                    {dados.contas_pagar_atraso.length === 0
                      ? <p className="text-success text-center py-4 mb-0"><i className="fas fa-check-circle me-2"></i>Nenhuma conta em atraso!</p>
                      : <TabelaContas lista={dados.contas_pagar_atraso} tipo="pagar" />}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── CONTAS A PAGAR DO DIA ── */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm border-start border-warning border-4">
                <div
                  className="card-header bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setAbertoPagarHoje(!abertoPagarHoje)}
                >
                  <h5 className="card-title mb-0">
                    <i className="fas fa-calendar-day me-2 text-warning"></i>
                    Contas a Pagar Hoje
                    <span className="badge bg-warning text-dark ms-2">{dados.qtd_pagar_hoje}</span>
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <strong className="text-warning">{fmt(dados.total_pagar_hoje)}</strong>
                    <i className={`fas fa-chevron-${abertoPagarHoje ? 'up' : 'down'} text-muted`}></i>
                  </div>
                </div>
                {abertoPagarHoje && (
                  <div className="card-body p-0">
                    {dados.contas_pagar_hoje.length === 0
                      ? <p className="text-muted text-center py-4 mb-0">Nenhuma conta vence hoje</p>
                      : <TabelaContas lista={dados.contas_pagar_hoje} tipo="pagar" />}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── CONTAS A RECEBER EM ATRASO ── */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm border-start border-danger border-4">
                <div
                  className="card-header bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setAbertoReceberAtraso(!abertoReceberAtraso)}
                >
                  <h5 className="card-title mb-0">
                    <i className="fas fa-exclamation-circle me-2 text-danger"></i>
                    Contas a Receber — Atrasadas
                    <span className="badge bg-danger ms-2">{dados.qtd_receber_atraso ?? 0}</span>
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <strong className="text-danger">{fmt(dados.total_receber_atraso ?? 0)}</strong>
                    <i className={`fas fa-chevron-${abertoReceberAtraso ? 'up' : 'down'} text-muted`}></i>
                  </div>
                </div>
                {abertoReceberAtraso && (
                  <div className="card-body p-0">
                    {(dados.contas_receber_atraso ?? []).length === 0
                      ? <p className="text-success text-center py-4 mb-0"><i className="fas fa-check-circle me-2"></i>Nenhuma conta a receber em atraso!</p>
                      : <TabelaContas lista={dados.contas_receber_atraso} tipo="receber" />}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── CONTAS A RECEBER 15+15 (a vencer) ── */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm border-start border-success border-4">
                <div
                  className="card-header bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setAbertoReceberJanela(!abertoReceberJanela)}
                >
                  <h5 className="card-title mb-0">
                    <i className="fas fa-hand-holding-usd me-2 text-success"></i>
                    Contas a Receber — Janela 15+15 dias
                    <span className="badge bg-success ms-2">{dados.qtd_receber_15_15}</span>
                    <small className="text-muted ms-2" style={{ fontSize: '12px', fontWeight: 400 }}>
                      (hoje até {dados.periodo_15_15?.fim})
                    </small>
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <strong className="text-success">{fmt(dados.total_receber_15_15)}</strong>
                    <i className={`fas fa-chevron-${abertoReceberJanela ? 'up' : 'down'} text-muted`}></i>
                  </div>
                </div>
                {abertoReceberJanela && (
                  <div className="card-body p-0">
                    {/* 1ª Janela */}
                    <div className="border-bottom">
                      <div
                        className="d-flex justify-content-between align-items-center px-3 py-2 bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); setAbertoReceberJanela1(!abertoReceberJanela1); }}
                      >
                        <small className="fw-bold text-success">
                          <i className={`fas fa-chevron-${abertoReceberJanela1 ? 'down' : 'right'} me-2`}></i>
                          1ª Janela — Hoje até {janelasSplit.d15Label}
                          <span className="badge bg-success ms-2">{janelasSplit.receber1.length}</span>
                        </small>
                        <small className="fw-bold text-success">{fmt(somaValor(janelasSplit.receber1))}</small>
                      </div>
                      {abertoReceberJanela1 && (
                        janelasSplit.receber1.length === 0
                          ? <p className="text-muted text-center py-3 mb-0 small">Nenhuma conta nos próximos 15 dias</p>
                          : <TabelaContas lista={janelasSplit.receber1} tipo="receber" />
                      )}
                    </div>
                    {/* 2ª Janela */}
                    <div>
                      <div
                        className="d-flex justify-content-between align-items-center px-3 py-2 bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); setAbertoReceberJanela2(!abertoReceberJanela2); }}
                      >
                        <small className="fw-bold" style={{ color: '#00205b' }}>
                          <i className={`fas fa-chevron-${abertoReceberJanela2 ? 'down' : 'right'} me-2`}></i>
                          2ª Janela — {janelasSplit.d15Label} até {dados.periodo_15_15?.fim}
                          <span className="badge ms-2" style={{ backgroundColor: '#005f6b' }}>{janelasSplit.receber2.length}</span>
                        </small>
                        <small className="fw-bold" style={{ color: '#00205b' }}>{fmt(somaValor(janelasSplit.receber2))}</small>
                      </div>
                      {abertoReceberJanela2 && (
                        janelasSplit.receber2.length === 0
                          ? <p className="text-muted text-center py-3 mb-0 small">Nenhuma conta nos próximos 16-30 dias</p>
                          : <TabelaContas lista={janelasSplit.receber2} tipo="receber" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── CONTAS A PAGAR 15+15 (a vencer) ── */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm border-start border-primary border-4">
                <div
                  className="card-header bg-white d-flex justify-content-between align-items-center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setAbertoPagarJanela(!abertoPagarJanela)}
                >
                  <h5 className="card-title mb-0">
                    <i className="fas fa-file-invoice-dollar me-2 text-primary"></i>
                    Contas a Pagar — Janela 15+15 dias
                    <span className="badge bg-primary ms-2">{dados.qtd_pagar_15_15}</span>
                    <small className="text-muted ms-2" style={{ fontSize: '12px', fontWeight: 400 }}>
                      (hoje até {dados.periodo_15_15?.fim})
                    </small>
                  </h5>
                  <div className="d-flex align-items-center gap-3">
                    <strong className="text-primary">{fmt(dados.total_pagar_15_15)}</strong>
                    <i className={`fas fa-chevron-${abertoPagarJanela ? 'up' : 'down'} text-muted`}></i>
                  </div>
                </div>
                {abertoPagarJanela && (
                  <div className="card-body p-0">
                    {/* 1ª Janela */}
                    <div className="border-bottom">
                      <div
                        className="d-flex justify-content-between align-items-center px-3 py-2 bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); setAbertoPagarJanela1(!abertoPagarJanela1); }}
                      >
                        <small className="fw-bold text-primary">
                          <i className={`fas fa-chevron-${abertoPagarJanela1 ? 'down' : 'right'} me-2`}></i>
                          1ª Janela — Hoje até {janelasSplit.d15Label}
                          <span className="badge bg-primary ms-2">{janelasSplit.pagar1.length}</span>
                        </small>
                        <small className="fw-bold text-primary">{fmt(somaValor(janelasSplit.pagar1))}</small>
                      </div>
                      {abertoPagarJanela1 && (
                        janelasSplit.pagar1.length === 0
                          ? <p className="text-muted text-center py-3 mb-0 small">Nenhuma conta nos próximos 15 dias</p>
                          : <TabelaContas lista={janelasSplit.pagar1} tipo="pagar" />
                      )}
                    </div>
                    {/* 2ª Janela */}
                    <div>
                      <div
                        className="d-flex justify-content-between align-items-center px-3 py-2 bg-light"
                        style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); setAbertoPagarJanela2(!abertoPagarJanela2); }}
                      >
                        <small className="fw-bold" style={{ color: '#6610f2' }}>
                          <i className={`fas fa-chevron-${abertoPagarJanela2 ? 'down' : 'right'} me-2`}></i>
                          2ª Janela — {janelasSplit.d15Label} até {dados.periodo_15_15?.fim}
                          <span className="badge ms-2" style={{ backgroundColor: '#6610f2' }}>{janelasSplit.pagar2.length}</span>
                        </small>
                        <small className="fw-bold" style={{ color: '#6610f2' }}>{fmt(somaValor(janelasSplit.pagar2))}</small>
                      </div>
                      {abertoPagarJanela2 && (
                        janelasSplit.pagar2.length === 0
                          ? <p className="text-muted text-center py-3 mb-0 small">Nenhuma conta nos próximos 16-30 dias</p>
                          : <TabelaContas lista={janelasSplit.pagar2} tipo="pagar" />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── FLUXO DE CAIXA ── */}
          {dadosGrafico.length > 1 && (
            <div className="row mb-4">
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div
                    className="card-header bg-white"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">
                        <i className="fas fa-exchange-alt me-2 color-primary"></i>
                        <span className="text-white">Fluxo de Caixa — Projeção 15+15 dias</span>
                      </h5>
                      <div>
                        <span className="badge bg-light text-dark me-2">
                          <i className="fas fa-university me-1"></i>
                          Saldo atual: {fmt(dados.saldo_corrente_total)}
                        </span>
                        <span className="badge bg-light text-dark">
                          <i className="fas fa-calendar me-1"></i>
                          Até {dados.periodo_15_15?.fim}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <div style={{ minWidth: 700 }}>
                        <ResponsiveContainer width="100%" height={380}>
                          <ComposedChart data={dadosGrafico} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                            <YAxis
                              tickFormatter={(v) => {
                                if (Math.abs(v) >= 1000) return `R$${(v / 1000).toFixed(0)}k`;
                                return `R$${v.toFixed(0)}`;
                              }}
                              tick={{ fontSize: 11 }}
                            />
                            <Tooltip
                              formatter={(value, name) => [fmt(value), name]}
                              labelFormatter={(label) => `Data: ${label}`}
                            />
                            <Legend />
                            <ReferenceLine x="Atual" stroke="#6c757d" strokeDasharray="4 4" label={{ value: 'Hoje', position: 'top', fontSize: 11 }} />
                            <Bar dataKey="entradas" name="Entradas" fill="#28a745" opacity={0.85} radius={[3, 3, 0, 0]} />
                            <Bar dataKey="saidas" name="Saídas" fill="#dc3545" opacity={0.85} radius={[3, 3, 0, 0]} />
                            <Line
                              type="monotone"
                              dataKey="saldo"
                              name="Saldo Projetado"
                              stroke="#007bff"
                              strokeWidth={3}
                              dot={{ r: 4, fill: '#007bff' }}
                              activeDot={{ r: 6 }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="alert alert-info border-start border-4 mb-0 mt-3" style={{ borderColor: '#005f6b' }}>
                      <small>
                        <strong><i className="fas fa-info-circle me-1"></i>Como interpretar:</strong>
                        <ul className="mb-0 mt-1">
                          <li><span className="text-success">Barras verdes</span> = Entradas (contas a receber com vencimento no dia)</li>
                          <li><span className="text-danger">Barras vermelhas</span> = Saídas (contas a pagar com vencimento no dia)</li>
                          <li><span className="text-primary">Linha azul</span> = Saldo projetado acumulado</li>
                        </ul>
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── RODAPÉ ── */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="card border-0 bg-light">
                <div className="card-body py-2">
                  <div className="row text-center">
                    <div className="col-md-3">
                      <small className="text-muted">
                        <i className="fas fa-university me-1 text-primary"></i>
                        <strong>Saldo:</strong> Contas com Posição = Sim
                      </small>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">
                        <i className="fas fa-exclamation-triangle me-1 text-danger"></i>
                        <strong>Atraso:</strong> Vencimento anterior a hoje
                      </small>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">
                        <i className="fas fa-arrows-alt-h me-1 text-info"></i>
                        <strong>Janela 15+15:</strong> {dados.periodo_15_15?.inicio} — {dados.periodo_15_15?.fim}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Classificação da conta na Posição: o cadastro grava a categoria com o texto da
// tela ("Cartão de Crédito", "Conta Aplicação"...), então comparar com 'credito'
// nunca casava e o cartão entrava como dinheiro disponível. Comparação sem acento
// e por conteúdo; `tipo` é reserva quando a categoria vem vazia.
const classeConta = (conta) => {
  const norm = (v) => (v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  for (const valor of [norm(conta?.categoria), norm(conta?.tipo)]) {
    if (!valor) continue;
    if (valor.includes('credito') || valor.includes('cartao')) return 'credito';
    if (valor.includes('investimento') || valor.includes('aplicacao') || valor.startsWith('cdb')) return 'investimento';
    return 'corrente';
  }
  return 'corrente';
};

export default Posicao_dia;
